// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useState, useEffect, useCallback, useRef } from 'react';
import { createSession, getActiveSession } from '../services/coverageService';
import type { CoverageSession, CreateCoverageSessionRequest } from '../types';

interface UseCoverageSessionResult {
  /** The site's active onboarding session; null when none exists (a normal
   * state — the wizard starts at step one), or while loading/errored. */
  session: CoverageSession | null;
  loading: boolean;
  /** Set when the coverage service is unreachable or errored (not for the
   * no-active-session case). */
  error: string | null;
  /** Create a session for the site (used by the wizard's first save). */
  create: (_request?: CreateCoverageSessionRequest) => Promise<CoverageSession>;
  refetch: () => void;
  /** Background refresh: re-resolves the session without toggling `loading`,
   * so pollers can keep the document map current without flashing the page.
   * Silent failures keep the last-known session. */
  refresh: () => void;
}

/**
 * Resolves the active coverage session for a site on mount (and on refetch).
 * Session creation is deliberately explicit — the wizard creates lazily on
 * the first site-profile save, never on page entry.
 */
export function useCoverageSession(
  siteId: string | undefined,
  enabled = true
): UseCoverageSessionResult {
  const [session, setSession] = useState<CoverageSession | null>(null);
  const [loading, setLoading] = useState(enabled && Boolean(siteId));
  const [error, setError] = useState<string | null>(null);
  const [fetchKey, setFetchKey] = useState(0);
  const silentRef = useRef(false);

  const refetch = useCallback(() => {
    silentRef.current = false;
    setFetchKey(k => k + 1);
  }, []);

  const refresh = useCallback(() => {
    silentRef.current = true;
    setFetchKey(k => k + 1);
  }, []);

  useEffect(() => {
    if (!enabled || !siteId) {
      setSession(null);
      setLoading(false);
      return;
    }

    const silent = silentRef.current;
    silentRef.current = false;
    let cancelled = false;

    (async () => {
      try {
        if (!silent) {
          setLoading(true);
          setError(null);
        }
        const result = await getActiveSession(siteId);
        // A silent refresh never clears a known session: resolving to null
        // mid-flow (session ended server-side) would unmount the current step
        // before the checklist's terminal 404 notice can explain what
        // happened. The checklist path owns that UX; explicit refetches and
        // reloads still observe the null.
        if (!cancelled && (result !== null || !silent)) setSession(result);
      } catch (err) {
        if (!cancelled && !silent) {
          setError((err as Error).message ?? 'Failed to load coverage session');
        }
      } finally {
        if (!cancelled && !silent) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [siteId, enabled, fetchKey]);

  const create = useCallback(
    async (request: CreateCoverageSessionRequest = {}) => {
      if (!siteId) throw new Error('No site selected');
      const created = await createSession(siteId, { site_key: siteId, ...request });
      setSession(created);
      return created;
    },
    [siteId]
  );

  return { session, loading, error, create, refetch, refresh };
}
